import { isPerpInstrument } from '@/features/hedge/lib/instrument-display'
import {
  resolveBaseCurrency,
  resolveQuoteCurrency,
} from '@/features/order/order-form/lib/instrument-resolution'
import { Instrument, InstrumentKind, OptionType } from '@/types/generated/common/types'

const resolveBase = (instrument: Instrument) =>
  resolveBaseCurrency([], instrument.symbol, instrument)

export const resolveQuote = (instrument: Instrument) =>
  resolveQuoteCurrency([], instrument.symbol, instrument)

const composeOptionFamily = (base: string, quote: string) =>
  quote && quote !== 'USD' && quote !== base ? `${base}_${quote}` : base

const resolveOptionFamily = (instrument: Instrument) =>
  composeOptionFamily(resolveBase(instrument), resolveQuote(instrument))

const resolveOptionDisplayQuote = (instrument: Instrument) => {
  const family = resolveOptionFamily(instrument)
  const separatorIndex = family.lastIndexOf('_')
  return separatorIndex >= 0 ? family.slice(separatorIndex + 1) : 'USD'
}

const matchesOptionFamily = (instrument: Instrument, base: string, quote: string) =>
  resolveOptionFamily(instrument) === composeOptionFamily(base.toUpperCase(), quote.toUpperCase())

function resolveOptionKind(instrument: Instrument): UnifiedInstrumentKind {
  if (instrument.optionType === OptionType.OPTION_TYPE_CALL) return 'option-call'
  if (instrument.optionType === OptionType.OPTION_TYPE_PUT) return 'option-put'
  return /-C(?:-[A-Z]+)?$/i.test(instrument.symbol) ? 'option-call' : 'option-put'
}

export type UnifiedInstrumentKind = 'spot' | 'future' | 'perp' | 'option-call' | 'option-put'

export function getAvailableQuotes(
  instruments: Instrument[],
  baseCurrency: string,
  kind?: UnifiedInstrumentKind
): string[] {
  const quotes = new Set<string>()
  instruments.forEach((inst) => {
    const base = resolveBase(inst)
    if (kind !== undefined && getInstrumentUnifiedKind(inst) !== kind) return
    if (base !== baseCurrency.toUpperCase()) return
    const quote =
      inst.kind === InstrumentKind.INSTRUMENT_KIND_OPTION
        ? resolveOptionDisplayQuote(inst)
        : resolveQuote(inst)
    if (quote === baseCurrency.toUpperCase()) return
    quotes.add(quote)
  })
  return Array.from(quotes).sort()
}

const KIND_ORDER: UnifiedInstrumentKind[] = ['perp', 'spot', 'future', 'option-call', 'option-put']

export function getAvailableKinds(
  instruments: Instrument[],
  baseCurrency: string,
  quoteCurrency: string
): UnifiedInstrumentKind[] {
  const kinds = new Set<UnifiedInstrumentKind>()
  instruments.forEach((inst) => {
    const baseMatch = resolveBase(inst) === baseCurrency.toUpperCase()
    const quoteMatch = resolveQuote(inst) === quoteCurrency.toUpperCase()

    if (baseMatch && quoteMatch) {
      if (inst.kind === InstrumentKind.INSTRUMENT_KIND_SPOT) kinds.add('spot')
      else if (inst.kind === InstrumentKind.INSTRUMENT_KIND_FUTURE) {
        if (isPerpInstrument(inst)) kinds.add('perp')
        else kinds.add('future')
      }
    }
    if (
      baseMatch &&
      inst.kind === InstrumentKind.INSTRUMENT_KIND_OPTION &&
      matchesOptionFamily(inst, baseCurrency, quoteCurrency)
    ) {
      kinds.add(resolveOptionKind(inst))
    }
  })

  return KIND_ORDER.filter((kind) => kinds.has(kind))
}

export function getInstrumentUnifiedKind(inst: Instrument): UnifiedInstrumentKind {
  if (inst.kind === InstrumentKind.INSTRUMENT_KIND_SPOT) return 'spot'
  if (inst.kind === InstrumentKind.INSTRUMENT_KIND_FUTURE) {
    return isPerpInstrument(inst) ? 'perp' : 'future'
  }
  if (inst.kind === InstrumentKind.INSTRUMENT_KIND_OPTION) {
    return resolveOptionKind(inst)
  }
  return 'perp'
}

export function findInstrument(
  instruments: Instrument[],
  base: string,
  quote: string,
  kind: UnifiedInstrumentKind
): Instrument | null {
  return (
    instruments.find((inst) => {
      if (resolveBase(inst) !== base.toUpperCase()) return false
      if (kind === 'option-call') {
        return (
          inst.kind === InstrumentKind.INSTRUMENT_KIND_OPTION &&
          matchesOptionFamily(inst, base, quote) &&
          resolveOptionKind(inst) === 'option-call'
        )
      }
      if (kind === 'option-put') {
        return (
          inst.kind === InstrumentKind.INSTRUMENT_KIND_OPTION &&
          matchesOptionFamily(inst, base, quote) &&
          resolveOptionKind(inst) === 'option-put'
        )
      }
      if (resolveQuote(inst) !== quote.toUpperCase()) return false
      if (kind === 'spot') return inst.kind === InstrumentKind.INSTRUMENT_KIND_SPOT
      if (kind === 'perp') {
        return inst.kind === InstrumentKind.INSTRUMENT_KIND_FUTURE && isPerpInstrument(inst)
      }
      if (kind === 'future') {
        return inst.kind === InstrumentKind.INSTRUMENT_KIND_FUTURE && !isPerpInstrument(inst)
      }
      return false
    }) || null
  )
}
